import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import {
  ArrowLeftIcon as ArrowLeft,
  GraphIcon as Graph,
  MagnifyingGlassIcon as Search,
  CircleNotchIcon as Spinner,
  ArrowUpIcon as ArrowUp,
  ArrowDownIcon as ArrowDown,
  WarningIcon as Warning,
  StackIcon as Stack,
  HexagonIcon as Hexagon,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import { getImpactGraph, getWorkspace } from "@/lib/api";
import { EMPTY_TEXT, groupImpactNodes, kindLabel } from "@/lib/impactNodes";
import MermaidDiagram from "@/components/MermaidDiagram";
import ZoomablePane from "@/components/metadata/ZoomablePane";
import ImpactKindTag from "@/components/metadata/ImpactKindTag";
import EdgeLegend from "@/components/metadata/EdgeLegend";

const DEPTHS = [1, 2, 3];

export default function WorkspaceMetadataImpact() {
  const { id } = useParams();
  const [params, setParams] = useSearchParams();
  const [workspace, setWorkspace] = useState(null);
  const [graph, setGraph] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const queryObject = params.get("object") || "";
  const queryLibrary = params.get("library") || "";
  const queryDepth = Number(params.get("depth")) || 2;
  const queryMaxNodes = Number(params.get("max_nodes")) || 80;
  const includeFields = params.get("include_fields") === "true"; // Iter 41
  const fromPage = params.get("from") || "";
  const [pendingObject, setPendingObject] = useState(queryObject);
  const [pendingLibrary, setPendingLibrary] = useState(queryLibrary);

  useEffect(() => {
    getWorkspace(id).then((d) => setWorkspace(d?.workspace || null));
  }, [id]);

  useEffect(() => {
    if (!queryObject) {
      setGraph(null);
      setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    getImpactGraph(id, {
      object: queryObject,
      library: queryLibrary || undefined,
      depth: queryDepth,
      maxNodes: queryMaxNodes,
      includeFields,
    })
      .then(setGraph)
      .catch((e) => {
        setGraph(null);
        setError(e?.response?.data?.detail || "Could not build impact graph.");
      })
      .finally(() => setLoading(false));
  }, [id, queryObject, queryLibrary, queryDepth, queryMaxNodes, includeFields]);

  const submitSearch = (e) => {
    e?.preventDefault?.();
    const obj = pendingObject.trim().toUpperCase();
    if (!obj) {
      toast.error("Enter an object name (e.g. CUSTMAST).");
      return;
    }
    const next = new URLSearchParams(params);
    next.set("object", obj);
    if (pendingLibrary.trim()) next.set("library", pendingLibrary.trim().toUpperCase());
    else next.delete("library");
    next.set("depth", String(queryDepth));
    setParams(next, { replace: false });
  };

  const setDepth = (d) => {
    const next = new URLSearchParams(params);
    next.set("depth", String(d));
    setParams(next, { replace: true });
  };

  const toggleFields = (on) => {
    const next = new URLSearchParams(params);
    if (on) next.set("include_fields", "true"); else next.delete("include_fields");
    setParams(next, { replace: true });
  };

  const handleShowMore = () => {
    // Double the node cap, clamped at the backend ceiling.
    const nextCap = Math.min(250, Math.max(queryMaxNodes * 2, 150));
    const next = new URLSearchParams(params);
    next.set("max_nodes", String(nextCap));
    setParams(next, { replace: true });
  };

  const stats = graph?.stats || {};
  const root = graph?.root;
  // Iter 43 — every node lands in exactly one panel (parsed / unknown kinds included).
  const { programs, files, fields: fieldNodes, others } = useMemo(() => groupImpactNodes(graph), [graph]);
  const fieldEdgeTypes = useMemo(() => {
    const m = {};
    for (const e of graph?.edges || []) {
      if (e.target?.startsWith("FIELD/")) (m[e.target] ||= new Set()).add(e.type);
    }
    return m;
  }, [graph]);

  return (
    <div className="space-y-6" data-testid="impact-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          {fromPage === "opportunities" ? (
            <Link
              to={`/workspaces/${id}/metadata/opportunities`}
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1"
              data-testid="impact-back-to-opportunities"
            >
              <ArrowLeft size={11} weight="bold" />
              Back to AI Readiness findings
            </Link>
          ) : fromPage === "findings" ? (
            <Link
              to="/findings"
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline inline-flex items-center gap-1"
              data-testid="impact-back-to-findings"
            >
              <ArrowLeft size={11} weight="bold" />
              Back to cross-workspace findings
            </Link>
          ) : (
            <Link
              to={`/workspaces/${id}/metadata/dashboard`}
              className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] hover:text-[var(--ml-primary)] inline-flex items-center gap-1"
              data-testid="impact-back-to-dashboard"
            >
              <ArrowLeft size={11} weight="bold" />
              Back to dashboard
            </Link>
          )}
          <h1 className="text-3xl sm:text-4xl font-semibold leading-tight tracking-tight mt-3">
            <Graph size={26} weight="duotone" color="#0f62fe" className="inline-block mr-2 align-text-bottom" />
            Impact analysis
          </h1>
          <p className="text-sm text-[var(--ml-text-secondary)] mt-1 max-w-2xl">
            Trace the call-graph around any file or program. Edges come from
            <code className="font-mono text-xs"> program_references </code>
            (DSPPGMREF imports and parsed RPG / CL / DDS source under
            <code className="font-mono text-xs"> *PARSED</code>). Bounded BFS — depth and node-count are capped to keep
            the diagram readable.
          </p>
        </div>
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] text-right">
          Workspace<br />
          <span className="text-[var(--ml-text-primary)]">{workspace?.name || "—"}</span>
        </div>
      </div>

      {/* Search bar */}
      <form
        onSubmit={submitSearch}
        className="ml-card p-4 flex flex-wrap items-end gap-3"
        data-testid="impact-search-form"
      >
        <div className="flex-1 min-w-[180px]">
          <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
            Object name
          </label>
          <div className="relative">
            <Search size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[var(--ml-text-secondary)]" />
            <input
              type="text"
              value={pendingObject}
              onChange={(e) => setPendingObject(e.target.value)}
              placeholder="e.g. CUSTMAST or ORDENTRY"
              className="ml-input pl-8 w-full font-mono uppercase"
              data-testid="impact-object-input"
              autoFocus
            />
          </div>
        </div>
        <div className="w-28">
          <label className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
            Library (opt)
          </label>
          <input
            type="text"
            value={pendingLibrary}
            onChange={(e) => setPendingLibrary(e.target.value)}
            placeholder="PRODLIB"
            className="ml-input w-full font-mono uppercase"
            data-testid="impact-library-input"
          />
        </div>
        <div>
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)] block mb-1">
            Depth
          </div>
          <div className="flex border border-[var(--ml-border)]" data-testid="impact-depth-selector">
            {DEPTHS.map((d) => (
              <button
                key={d}
                type="button"
                onClick={() => setDepth(d)}
                data-testid={`impact-depth-${d}`}
                data-active={queryDepth === d}
                className={`px-3 py-2 text-sm font-mono border-r last:border-r-0 border-[var(--ml-border)] ${
                  queryDepth === d ? "bg-[var(--ml-primary)] text-white" : "bg-white text-[var(--ml-text-primary)] hover:bg-[var(--ml-surface)]"
                }`}
              >
                {d}
              </button>
            ))}
          </div>
        </div>
        <button
          type="submit"
          data-testid="impact-search-btn"
          className="inline-flex items-center gap-2 px-4 py-2 bg-[var(--ml-primary)] text-white text-sm font-medium hover:bg-[var(--ml-primary-hover)] transition-colors"
        >
          <Graph size={14} weight="bold" /> Trace
        </button>
        <label className="inline-flex items-center gap-2 text-xs cursor-pointer select-none pb-2" title="Overlay deterministic field references (Iter 41) as hexagon nodes; edges carry the reference type">
          <input
            type="checkbox"
            checked={includeFields}
            onChange={(e) => toggleFields(e.target.checked)}
            data-testid="impact-include-fields"
          />
          Include fields
        </label>
      </form>

      {!queryObject ? (
        <EmptyState />
      ) : loading ? (
        <div className="ml-card p-12 flex items-center justify-center text-sm text-[var(--ml-text-secondary)]">
          <Spinner size={18} className="animate-spin mr-2" /> Walking the graph…
        </div>
      ) : error ? (
        <div className="ml-card p-6 border-l-2 border-[var(--ml-error)] flex items-start gap-3" data-testid="impact-error">
          <Warning size={20} weight="duotone" color="#a2191f" />
          <div>
            <div className="font-medium mb-1">Could not build impact graph</div>
            <p className="text-sm text-[var(--ml-text-secondary)]">{error}</p>
          </div>
        </div>
      ) : graph ? (
        <>
          <StatsStrip stats={stats} root={root} onShowMore={handleShowMore} />
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-4" data-testid="impact-content">
            <div className="lg:col-span-2 space-y-4">
              <div data-testid="impact-diagram-wrap">
                <ZoomablePane>
                  <MermaidDiagram code={graph.mermaid} bare />
                </ZoomablePane>
              </div>
              <EdgeLegend graph={graph} />
            </div>
            <aside className="space-y-4">
              <NeighborList
                items={programs}
                workspaceId={id}
                depth={queryDepth}
                icon={ArrowUp}
                iconColor="#a2191f"
                title="Programs in the blast-radius"
                emptyText={EMPTY_TEXT.programs}
                testid="impact-neighbors-programs"
              />
              <NeighborList
                items={files}
                workspaceId={id}
                depth={queryDepth}
                icon={ArrowDown}
                iconColor="#0f62fe"
                title="Files in the blast-radius"
                emptyText={EMPTY_TEXT.files}
                testid="impact-neighbors-files"
              />
              {others.length > 0 && (
                <NeighborList
                  items={others}
                  workspaceId={id}
                  depth={queryDepth}
                  icon={Stack}
                  iconColor="#6f6f6f"
                  title="Other objects in the blast-radius"
                  emptyText={EMPTY_TEXT.others}
                  testid="impact-neighbors-others"
                />
              )}
              {includeFields && (
                <FieldNodeList items={fieldNodes} edgeTypes={fieldEdgeTypes} workspaceId={id} stats={stats} rootProgram={root?.kind === "program" ? root.name : null} />
              )}
            </aside>
          </div>
        </>
      ) : null}
    </div>
  );
}

function EmptyState() {
  return (
    <div className="ml-card p-12 text-center" data-testid="impact-empty">
      <Stack size={36} weight="duotone" color="#0f62fe" className="mx-auto mb-3" />
      <div className="font-medium mb-1">Pick an object to trace</div>
      <p className="text-sm text-[var(--ml-text-secondary)] max-w-md mx-auto">
        Enter a file or program name above. The graph walks both upstream
        (callers) and downstream (referenced) directions to <strong>depth 2</strong> by default.
      </p>
    </div>
  );
}

function StatsStrip({ stats, root, onShowMore }) {
  return (
    <div
      className="flex flex-wrap items-center gap-x-5 gap-y-2 border-l-2 border-[var(--ml-primary)] bg-[var(--ml-surface)] px-3 py-2 text-sm"
      data-testid="impact-stats"
    >
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        Root
      </span>
      <span className="font-mono">
        <span className="text-[var(--ml-text-secondary)]">{root?.library}/</span>
        {root?.name}
      </span>
      <ImpactKindTag node={root} testid="impact-root-kind" />
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="impact-node-count">
        <strong className="text-[var(--ml-text-primary)]">{stats.node_count}</strong> nodes
      </span>
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="impact-edge-count">
        <strong className="text-[var(--ml-text-primary)]">{stats.edge_count}</strong> edges
      </span>
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        <ArrowUp size={11} weight="bold" className="inline" /> {stats.upstream_count} upstream
      </span>
      <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
        <ArrowDown size={11} weight="bold" className="inline" /> {stats.downstream_count} downstream
      </span>
      {stats.parsed_count > 0 && (
        <span className="ml-mono text-[10px] uppercase tracking-widest" style={{ color: "#8a6d00" }} data-testid="impact-parsed-count" title="Objects known only from parsed source (no DSPOBJD / DSPFD catalog row)">
          <strong>{stats.parsed_count}</strong> parsed source
        </span>
      )}
      {stats.by_kind?.unknown > 0 && (
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]" data-testid="impact-unknown-count" title="Objects whose kind cannot be proven from catalog, parsed source or reference type">
          <strong className="text-[var(--ml-text-primary)]">{stats.by_kind.unknown}</strong> {kindLabel("unknown").toLowerCase()} kind
        </span>
      )}
      {stats.truncated && (
        <span className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-warning)] flex items-center gap-1.5" data-testid="impact-truncated">
          ⚠ truncated at {stats.max_nodes} nodes
          {stats.max_nodes < 250 && onShowMore && (
            <button
              type="button"
              onClick={onShowMore}
              data-testid="impact-show-more-btn"
              className="ml-1 px-1.5 py-0.5 border border-[var(--ml-warning)] text-[var(--ml-warning)] hover:bg-[var(--ml-warning)] hover:text-white transition-colors"
              title="Re-walk with a larger node cap (max 250)"
            >
              Show more →
            </button>
          )}
        </span>
      )}
    </div>
  );
}

function FieldNodeList({ items, edgeTypes, workspaceId, stats, rootProgram }) {
  const screenNodes = items.filter((n) => n.display_file);
  return (
    <div className="ml-card p-4" data-testid="impact-neighbors-fields">
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2">
          <Hexagon size={14} weight="bold" color="#8a3ffc" />
          <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
            Fields touched ({items.length}){stats?.truncated_fields ? " · truncated" : ""}
          </div>
        </div>
        {screenNodes.length > 0 && (
          <Link
            to={`/workspaces/${workspaceId}/metadata/screen-walkthrough${rootProgram ? `?program=${encodeURIComponent(rootProgram)}` : ""}`}
            className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline shrink-0"
            data-testid="impact-walkthrough-link"
            title={`${screenNodes.length} screen field${screenNodes.length === 1 ? "" : "s"} in this graph — open the screen walkthrough`}
          >
            screen walkthrough →
          </Link>
        )}
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid="impact-neighbors-fields-empty">{EMPTY_TEXT.fields}</p>
      ) : (
        <ul className="text-sm space-y-1" data-testid="impact-neighbors-fields-list">
          {items.map((n) => {
            const scope = `${n.file || "UNRESOLVED"}-${n.name}`;
            const access = n.access?.length ? n.access : [...(edgeTypes[n.id] || [])].filter((t) => t !== "in");
            return (
            <li key={n.id} className="flex items-center justify-between gap-2 border-b border-[var(--ml-border)] last:border-b-0 py-1.5" data-testid={`impact-field-${scope}`} data-field={n.name} data-confidence={n.confidence}>
              <span className="font-mono text-xs min-w-0 truncate" title={n.text || ""}>
                {n.name}
                <span className="text-[var(--ml-text-secondary)]"> · {n.file || (n.ambiguous ? "ambiguous" : "unresolved")}</span>
                {access.length > 0 && (
                  <span className="text-[var(--ml-text-secondary)]" data-testid={`impact-field-access-${scope}`}> · {access.join(", ")}</span>
                )}
              </span>
              <Link
                to={`/workspaces/${workspaceId}/metadata/field-dependencies?impact=${encodeURIComponent(n.name)}${n.file ? `&impact_file=${encodeURIComponent(n.file)}` : ""}`}
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline shrink-0"
                data-testid={`impact-field-link-${scope}`}
              >
                impact →
              </Link>
            </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}

function NeighborList({ items, icon: Icon, iconColor, title, emptyText, testid, workspaceId, depth = 2 }) {
  return (
    <div className="ml-card p-4" data-testid={testid} data-count={items.length}>
      <div className="flex items-center gap-2 mb-2">
        <Icon size={14} weight="bold" color={iconColor} />
        <div className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-text-secondary)]">
          {title} ({items.length})
        </div>
      </div>
      {items.length === 0 ? (
        <p className="text-xs text-[var(--ml-text-secondary)]" data-testid={`${testid}-empty`}>{emptyText}</p>
      ) : (
        <ul className="text-sm space-y-1" data-testid={`${testid}-list`}>
          {items.map((n) => (
            <li
              key={n.id}
              className="flex items-center justify-between gap-2 border-b border-[var(--ml-border)] last:border-b-0 py-1.5"
              data-testid={`impact-neighbor-${n.library}-${n.name}`}
              data-kind={n.kind}
              data-origin={n.origin || "unknown"}
            >
              <span className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
                <span className="font-mono text-xs min-w-0 truncate" title={n.text || ""}>
                  <span className="text-[var(--ml-text-secondary)]">{n.library}/</span>
                  {n.name}
                </span>
                <ImpactKindTag node={n} testid={`impact-neighbor-kind-${n.library}-${n.name}`} />
              </span>
              <Link
                to={`/workspaces/${workspaceId}/metadata/impact?object=${encodeURIComponent(n.name)}&library=${encodeURIComponent(n.library)}&depth=${depth}`}
                className="ml-mono text-[10px] uppercase tracking-widest text-[var(--ml-primary)] hover:underline shrink-0"
                data-testid={`impact-pivot-${n.library}-${n.name}`}
              >
                trace →
              </Link>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
